import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { startTry, gradeTry, TryQuestion, TrySubmitAnswer, TryGradeResult } from '../../api/try';
import { track } from '../../api/analytics';
import { questionToHtml, renderOption } from '../../utils/richText';
import { splitSentenceAtWord } from '../../utils/spellingGap';
import { QuizTopBar, QuizProgressStrip, QuizOption, QuizNavBar, QuizBackButton, QuizPrimaryButton } from '../quiz/QuizAtoms';
import '../PlacementQuiz.css';
import './Try.css';

// The DB `title` column is a SHORT (truncated, "…") label — never render it
// when the full stem is available in content (same rule as PlacementQuiz).
function questionStem(q: TryQuestion): string {
  const c = (q.content || {}) as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : '');
  return pick(c.question) || pick(c.questionText) || pick(c.stem) || pick(c.text) || q.title || '';
}

function responseFormatOf(q: TryQuestion): string {
  return String((q.content as Record<string, unknown>)?.responseFormat || '');
}

/** spelling/cloze items — mcq answer options are empty for these (ROO-142 API contract). */
function isTextResponse(q: TryQuestion): boolean {
  return responseFormatOf(q) === 'text';
}
function isNumericResponse(q: TryQuestion): boolean {
  return responseFormatOf(q) === 'numeric';
}

/** multiple_choice values are JSON-encoded arrays on the wire (same convention
 * the existing student-facing submit routes use). Falls back to [raw] so a
 * plain string doesn't blow up JSON.parse. */
function parseMultiValue(raw: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [raw];
  } catch {
    return [raw];
  }
}

const inputStyle: React.CSSProperties = {
  fontSize: 16,
  padding: '10px 14px',
  border: '1.5px solid var(--line-strong)',
  borderRadius: 10,
  width: '100%',
  maxWidth: 320,
  fontFamily: 'inherit',
};

const SUBJECT_LABEL: Record<string, string> = {
  maths: 'Maths',
  english: 'English',
};

const ArrowRight = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14m-6-6 6 6-6 6" />
  </svg>
);
const ArrowLeft = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5m6 6-6-6 6-6" />
  </svg>
);

/**
 * Public, no-signup 3-question sample quiz (ROO-142): /try/:subject
 *
 * Anonymous — no studentId, no resume cache, grading happens server-side via
 * a tryToken (never client-side, so the correct answer never leaks before
 * submission). Handles mcq (single/multiple_choice), text (spelling/cloze
 * gap-fill) and numeric response formats — content.responseFormat drives the
 * input, same convention as the rest of the portal's quizzes (ROO-180).
 */
const TryQuiz: React.FC = () => {
  const { subject = '' } = useParams<{ subject: string }>();
  const navigate = useNavigate();
  const subjectLabel = SUBJECT_LABEL[subject] || subject;

  const [phase, setPhase] = useState<'loading' | 'quiz' | 'submitting' | 'result' | 'error'>('loading');
  const [questions, setQuestions] = useState<TryQuestion[]>([]);
  const [tryToken, setTryToken] = useState<string>('');
  const [currentIdx, setCurrentIdx] = useState(0);
  // questionId -> raw wire-format value (plain string, or JSON-encoded array for multiple_choice)
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TryGradeResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    startTry(subject)
      .then((session) => {
        if (cancelled) return;
        const qs = session.questions || [];
        if (qs.length === 0) {
          setErrorMsg("We couldn't find any sample questions for that subject right now. Please try again shortly.");
          setPhase('error');
          return;
        }
        setQuestions(qs);
        setTryToken(session.tryToken);
        setAnswers({});
        setCurrentIdx(0);
        setPhase('quiz');
        track('trySampleStarted', { properties: { subject, questionCount: qs.length } });
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMsg(err?.response?.data?.error || err?.message || 'Failed to load sample questions. Please try again.');
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [subject]);

  const total = questions.length;
  const progressPct = total > 0 ? Math.round((currentIdx / total) * 100) : 0;
  const currentQuestion: TryQuestion | undefined = questions[currentIdx];
  const isMulti = currentQuestion?.type === 'multiple_choice';
  const isChoice = !!currentQuestion && !isTextResponse(currentQuestion) && !isNumericResponse(currentQuestion);

  const rawValue = (currentQuestion && answers[currentQuestion.id]) || '';
  const selectedIds: string[] = isMulti ? parseMultiValue(rawValue) : rawValue ? [rawValue] : [];
  const hasAnswer = isChoice ? selectedIds.length > 0 : rawValue.trim().length > 0;

  const setAnswer = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const selectOption = (value: string) => {
    if (!currentQuestion) return;
    if (isMulti) {
      const next = selectedIds.includes(value) ? selectedIds.filter((v) => v !== value) : [...selectedIds, value];
      setAnswer(currentQuestion.id, next.length ? JSON.stringify(next) : '');
    } else {
      setAnswer(currentQuestion.id, value);
    }
  };

  const handleNext = () => {
    if (currentIdx < total - 1) setCurrentIdx((i) => i + 1);
  };
  const handleBack = () => {
    if (currentIdx > 0) setCurrentIdx((i) => i - 1);
  };

  const handleFinish = useCallback(async () => {
    setPhase('submitting');
    try {
      const submitted: TrySubmitAnswer[] = questions
        .filter((q) => (answers[q.id] || '').length > 0)
        .map((q) => ({ questionId: q.id, answer: answers[q.id] }));
      const res = await gradeTry(tryToken, submitted);
      setResult(res);
      setPhase('result');
      track('trySampleCompleted', { properties: { subject, score: res.score, total: res.total } });
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err?.message || 'Failed to grade your answers. Please try again.');
      setPhase('error');
    }
  }, [tryToken, answers, subject, questions]);

  const isLastQuestion = currentIdx === total - 1;
  const quizTitle = `Try ${subjectLabel || '3'} questions`;

  const backToPicker = () => navigate('/try');

  // ── loading ──
  if (phase === 'loading') {
    return (
      <div className="cvr-portal pq-shell">
        <QuizTopBar title={quizTitle} onCancel={backToPicker} cancelLabel="Back" />
        <div className="pq-wrap">
          <div className="pq-loading">
            <div className="pq-loading__spinner" />
            Loading your sample questions...
          </div>
        </div>
      </div>
    );
  }

  // ── error ──
  if (phase === 'error') {
    return (
      <div className="cvr-portal pq-shell">
        <QuizTopBar title={quizTitle} onCancel={backToPicker} cancelLabel="Back" />
        <div className="pq-wrap">
          <div className="pq-error">{errorMsg}</div>
          <div style={{ marginTop: 20 }}>
            <button className="pq-nav__back" type="button" onClick={backToPicker}>
              {ArrowLeft} Try another subject
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── result ──
  if (phase === 'result' && result) {
    const byId = new Map(questions.map((q) => [q.id, q]));
    return (
      <div className="cvr-portal pq-shell">
        <QuizTopBar title={quizTitle} right="Complete!" />
        <QuizProgressStrip pct={100} />
        <div className="try-result">
          <div className="try-result__score">
            <div className="try-result__score-num">{result.score}/{result.total}</div>
            <div className="try-result__score-label">
              {result.score === result.total
                ? 'Perfect score — amazing work!'
                : result.score > 0
                ? 'Nice start — here\'s how it went'
                : 'Everyone starts somewhere — here\'s how it went'}
            </div>
          </div>

          {result.results.map((item, i) => {
            const q = byId.get(item.questionId);
            if (!q) return null;
            const stemText = questionStem(q);
            const stemHtml = questionToHtml(q.content as Record<string, any>, stemText);
            const correctVals: string[] = Array.isArray(item.correctAnswer) ? item.correctAnswer : [item.correctAnswer];
            const submittedRaw = answers[item.questionId] || '';
            const qIsMulti = q.type === 'multiple_choice';
            const qIsChoice = !isTextResponse(q) && !isNumericResponse(q);
            const submittedVals: string[] = qIsMulti ? parseMultiValue(submittedRaw) : submittedRaw ? [submittedRaw] : [];
            return (
              <div key={item.questionId} className="try-result__item">
                <div className={`try-result__item-head ${item.correct ? 'try-result__item-head--correct' : 'try-result__item-head--wrong'}`}>
                  {item.correct ? '✓ Correct' : '✗ Not quite'} — Question {i + 1}
                </div>
                {stemHtml ? (
                  <div className="pq-question__text" style={{ fontSize: 15 }} dangerouslySetInnerHTML={{ __html: stemHtml }} />
                ) : (
                  <div className="pq-question__text" style={{ fontSize: 15 }}>{stemText}</div>
                )}
                {qIsChoice ? (
                  <div className="pq-options" style={{ marginTop: 10, pointerEvents: 'none' }}>
                    {(q.answers || []).map((opt) => {
                      const isCorrectOpt = correctVals.includes(opt.value);
                      const isWrongPick = submittedVals.includes(opt.value) && !isCorrectOpt;
                      return (
                        <QuizOption
                          key={opt.value}
                          selected={submittedVals.includes(opt.value)}
                          onClick={() => {}}
                          feedback={{ isCorrectOption: isCorrectOpt, isWrongPick }}
                          img={opt.img}
                          html={renderOption(opt.text)}
                          text={opt.text}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ marginTop: 10, fontSize: 13.5 }}>
                    <div style={{ color: '#6B7A6F' }}>
                      Your answer: <b style={{ color: item.correct ? '#14593C' : '#B83E24' }}>{submittedRaw || '(blank)'}</b>
                    </div>
                    {!item.correct && (
                      <div style={{ color: '#14593C', marginTop: 2 }}>
                        Correct answer: <b>{correctVals.join(', ')}</b>
                      </div>
                    )}
                  </div>
                )}
                {item.explanation && <div className="try-result__explanation">{item.explanation}</div>}
              </div>
            );
          })}

          <div className="try-result__cta">
            <h2 className="try-result__cta-title">Like what you saw?</h2>
            <p className="try-result__cta-sub">Get the full adaptive plan for your child — start your free 14-day trial, no card needed.</p>
            <Link className="try-result__cta-btn" to="/signup">Start your free 14-day trial {ArrowRight}</Link>
          </div>
          <Link className="try-result__foot-link" to="/try">‹ Try another subject</Link>
        </div>
      </div>
    );
  }

  // ── quiz ──
  if (!currentQuestion) return null;
  const submitting = phase === 'submitting';
  const stemText = questionStem(currentQuestion);

  return (
    <div className="cvr-portal pq-shell">
      <QuizTopBar
        title={quizTitle}
        onCancel={backToPicker}
        cancelDisabled={submitting}
        cancelLabel="Back"
        right={<>Question {currentIdx + 1} of {total}</>}
      />
      <QuizProgressStrip pct={progressPct} />

      <div className="pq-wrap">
        {submitting && (
          <div className="pq-loading">
            <div className="pq-loading__spinner" />
            Grading your answers...
          </div>
        )}

        {!submitting && (
          <div className="pq-question">
            {currentQuestion.subject && <div className="pq-question__subject">{subjectLabel}</div>}

            {/* Text-response (spelling/cloze) items render the sentence WITH its
                inline gap below — showing the plain stem here too would double
                it (ROO-212 rule), so skip the generic stem block for that type. */}
            {isChoice && (() => {
              const stemHtml = questionToHtml(currentQuestion.content as Record<string, any>, stemText);
              return stemHtml ? (
                <div className="pq-question__text" dangerouslySetInnerHTML={{ __html: stemHtml }} />
              ) : (
                <div className="pq-question__text">{stemText}</div>
              );
            })()}

            {isChoice && (
              <>
                {isMulti && <div className="pq-question__hint">Select all that apply</div>}
                <div className="pq-options" role={isMulti ? 'group' : 'radiogroup'} aria-label="Answer options">
                  {(currentQuestion.answers || []).map((opt, oi) => (
                    <QuizOption
                      key={opt.value}
                      selected={selectedIds.includes(opt.value)}
                      multi={isMulti}
                      onClick={() => selectOption(opt.value)}
                      img={opt.img}
                      html={renderOption(opt.text)}
                      text={opt.text}
                      index={oi + 1}
                    />
                  ))}
                </div>
              </>
            )}

            {isTextResponse(currentQuestion) && (() => {
              const misspelled = ((currentQuestion.content as any)?.misspelledWord || '').toString();
              const split = misspelled ? splitSentenceAtWord(stemText, misspelled) : null;
              if (split) {
                const gapWidth = Math.max(misspelled.length + 4, 10);
                return (
                  <>
                    <div className="pq-question__hint">
                      The word <s>{misspelled}</s> is spelled incorrectly. Type the correct spelling in the gap.
                    </div>
                    <div className="pq-question__text" style={{ lineHeight: 2.4 }}>
                      {split.before}
                      <input
                        value={rawValue}
                        onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
                        placeholder="…"
                        aria-label="Type the correct spelling"
                        style={{
                          display: 'inline-block',
                          width: `${gapWidth}ch`,
                          margin: '0 4px',
                          fontSize: 16,
                          textAlign: 'center',
                          border: '1.5px solid var(--line-strong)',
                          borderRadius: 8,
                          padding: '4px 6px',
                          fontFamily: 'inherit',
                        }}
                      />
                      {split.after}
                    </div>
                  </>
                );
              }
              // Fallback (no misspelledWord match): full sentence + separate input.
              return (
                <>
                  <div className="pq-question__text">{stemText}</div>
                  <input
                    value={rawValue}
                    onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
                    placeholder="Type the correct spelling..."
                    aria-label="Your answer"
                    style={{ ...inputStyle, marginTop: 10 }}
                  />
                </>
              );
            })()}

            {isNumericResponse(currentQuestion) && (() => {
              const stemHtml = questionToHtml(currentQuestion.content as Record<string, any>, stemText);
              return (
                <>
                  {stemHtml ? (
                    <div className="pq-question__text" dangerouslySetInnerHTML={{ __html: stemHtml }} />
                  ) : (
                    <div className="pq-question__text">{stemText}</div>
                  )}
                  <input
                    value={rawValue}
                    onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
                    placeholder="Enter your answer..."
                    inputMode="decimal"
                    aria-label="Your answer"
                    style={{ ...inputStyle, marginTop: 10 }}
                  />
                </>
              );
            })()}

            <QuizNavBar
              left={
                <QuizBackButton onClick={handleBack} disabled={currentIdx === 0}>
                  {ArrowLeft} Back
                </QuizBackButton>
              }
              right={
                isLastQuestion ? (
                  <QuizPrimaryButton onClick={handleFinish} disabled={!hasAnswer} finish>
                    See my results {ArrowRight}
                  </QuizPrimaryButton>
                ) : (
                  <QuizPrimaryButton onClick={handleNext} disabled={!hasAnswer}>
                    Next {ArrowRight}
                  </QuizPrimaryButton>
                )
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TryQuiz;
